import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  Car,
  ChevronLeft,
  ArrowRight,
  CheckCircle,
  Shield,
  Sparkles,
  Clock,
  Zap,
  Lock,
} from 'lucide-react';

const FREE_FEATURES = [
  'Published immediately',
  '1 active listing at a time',
  '30-day listing',
  'Up to 5 photos',
];

const COMP_FEATURES = [
  'Unlimited listings',
  'No expiry — stays live until sold',
  'Up to 20 photos',
  'PPSR check bundled',
  'Matched with buyers\u2019 Wants automatically',
  'Quality score up to 100',
  'AI ad generation + valuation (WANTD+)',
  'Earn 200 points on purchase',
];

export default function AutoListLandingPage({ slug = 'cars' }) {
  const navigate = useNavigate();
  const label = slug.charAt(0).toUpperCase() + slug.slice(1).replace(/s$/, '');

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to={`/auto/${slug}`} className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)]">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-[#1E3A5F]" />
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">List a {label}</h1>
            </div>
            <p className="text-sm text-[var(--theme-text-muted)]">
              Choose the listing type that suits you
            </p>
          </div>
        </div>

        {/* Options Grid */}
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Free Listing */}
          <button
            onClick={() => navigate(`/auto/${slug}/list/free`)}
            className="group p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#1E3A5F] transition-all text-left hover:shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center group-hover:bg-[#1E3A5F] transition-colors">
                <Zap className="w-7 h-7 text-[#1E3A5F] group-hover:text-white transition-colors" />
              </div>
              <span className="px-3 py-1 rounded-full bg-[#059669]/10 text-[#059669] text-sm font-bold">
                Free
              </span>
            </div>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-1">
              Quick &amp; Easy
            </h2>
            <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
              Get your car in front of buyers fast with a simple listing.
            </p>
            <ul className="space-y-2 mb-4">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)]">
                  <CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[var(--theme-text-muted)] mb-4 flex items-start gap-1.5">
              <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              Free ads won&apos;t automatically match with buyers&apos; Wants
            </p>
            <div className="flex items-center gap-2 text-[#1E3A5F] font-medium">
              <span>List for free</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Plus Listing */}
          <button
            onClick={() => navigate(`/auto/${slug}/list/plus`)}
            className="group p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#C9956C] transition-all text-left hover:shadow-lg relative overflow-hidden"
          >
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 rounded-full bg-[#C9956C]/10 text-[#C9956C] text-xs font-bold flex items-center gap-1">
                <Shield className="w-3 h-3" />
                PPSR
              </span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center group-hover:bg-[#C9956C] transition-colors">
                <Sparkles className="w-7 h-7 text-[#C9956C] group-hover:text-white transition-colors" />
              </div>
              <span className="px-3 py-1 rounded-full bg-[#C9956C]/10 text-[#C9956C] text-sm font-bold">
                $20
              </span>
            </div>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-1">
              Plus
            </h2>
            <p className="text-sm text-[var(--theme-text-secondary)] mb-4">
              The full package — PPSR, matching, unlimited listings, no expiry.
            </p>
            <ul className="space-y-2 mb-4">
              {COMP_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-[var(--theme-text-secondary)]">
                  <CheckCircle className="w-4 h-4 text-[#C9956C] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-[#C9956C] font-medium">
              <span>List for $20</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </div>
    </Layout>
  );
}
